import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import type React from "react";
import { type AdhocPreviewResultQuery } from "../../generated/graphql";

type ApplyPlan = NonNullable<
  AdhocPreviewResultQuery["adhocPreviewResult"]["applyPlan"]
>;

/**
 * Human gloss for each rung's mechanism string (the vocabularies are fixed by
 * the V3 apply planner). Unknown values fall through to the raw string so a new
 * mechanism is never silently hidden.
 */
export const BILLING_PARTY_MEANING: Record<string, string> = {
  SENDER_PREFILL: "Resolved from the sender address (zero-LLM prefill)",
  DE_CONTACT: "Resolved from the DE-extracted contact",
  ABSTAIN: "No billing party resolved — apply would short-circuit to FAILED",
};

export const SERVICE_MEANING: Record<string, string> = {
  CONTACT_DEFAULT: "The billing contact's default service",
  MODAL_PRIOR: "The company's prior for this modality",
  EXTRACTED: "Taken from an extracted field",
  UNSET: "No service resolved",
};

export const DEADLINE_TYPE_MEANING: Record<string, string> = {
  MAPPED: "A deadline type was extracted from the document",
  DEFAULT: "Fell back to the DUE_ON default",
};

export const meaningFor = (
  meanings: Record<string, string>,
  mechanism: string,
): string | null => meanings[mechanism] ?? null;

const styles = {
  paper: { p: 2 },
  sectionHeading: { mt: 2 },
  mechValue: { fontFamily: "monospace", fontSize: 13 },
  keyCell: { width: 160, verticalAlign: "top" },
} satisfies Record<string, SxProps<Theme>>;

/** One `label → value` row; renders a placeholder when the value is nullish. */
const Row = ({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) => (
  <TableRow>
    <TableCell sx={styles.keyCell}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </TableCell>
    <TableCell>{children}</TableCell>
  </TableRow>
);

/** A monospace mechanism chip beside its human gloss (gloss omitted if unknown). */
const Mechanism = ({
  mechanism,
  meaning,
}: {
  readonly mechanism: string;
  readonly meaning: string | null;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
    <Chip size="small" label={mechanism} sx={styles.mechValue} />
    {!isNil(meaning) && (
      <Typography variant="body2" color="text.secondary">
        {meaning}
      </Typography>
    )}
  </Stack>
);

const Mono = ({ value }: { readonly value: string | null | undefined }) =>
  isNil(value) ? (
    <Typography variant="body2" color="text.disabled">
      —
    </Typography>
  ) : (
    <Typography sx={styles.mechValue}>{value}</Typography>
  );

/**
 * Renders the side-effect-free apply plan the V3 apply WOULD make for the
 * previewed document: the billing-party, service, deadline-type, and duplicate
 * rungs. Service / deadline / duplicate are null when the billing-party rung
 * abstained (the apply short-circuits), so each is rendered defensively.
 *
 * @param props The component props.
 * @param props.applyPlan The previewed apply plan from `adhocPreviewResult`.
 */
const AdhocDecisionPanel = ({
  applyPlan,
}: {
  readonly applyPlan: ApplyPlan;
}) => {
  const { billingParty, service, deadlineType, duplicate } = applyPlan;
  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Typography gutterBottom variant="h6">
        Decision plan
      </Typography>

      <Typography gutterBottom variant="subtitle2">
        Billing party
      </Typography>
      <Table size="small">
        <TableBody>
          <Row label="Mechanism">
            <Mechanism
              mechanism={billingParty.mechanism}
              meaning={meaningFor(
                BILLING_PARTY_MEANING,
                billingParty.mechanism,
              )}
            />
          </Row>
          <Row label="Contact uuid">
            <Mono value={billingParty.contactUuid} />
          </Row>
          <Row label="Sender verdict">
            <Mono value={billingParty.senderVerdict} />
          </Row>
        </TableBody>
      </Table>

      <Typography gutterBottom variant="subtitle2" sx={styles.sectionHeading}>
        Service
      </Typography>
      {isNil(service) ? (
        <Typography variant="body2" color="text.secondary">
          Not planned (billing party abstained).
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            <Row label="Mechanism">
              <Mechanism
                mechanism={service.mechanism}
                meaning={meaningFor(SERVICE_MEANING, service.mechanism)}
              />
            </Row>
            <Row label="Service id">
              <Mono value={service.serviceId} />
            </Row>
          </TableBody>
        </Table>
      )}

      <Typography gutterBottom variant="subtitle2" sx={styles.sectionHeading}>
        Deadline type
      </Typography>
      {isNil(deadlineType) ? (
        <Typography variant="body2" color="text.secondary">
          Not planned (billing party abstained).
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            <Row label="Mechanism">
              <Mechanism
                mechanism={deadlineType.mechanism}
                meaning={meaningFor(
                  DEADLINE_TYPE_MEANING,
                  deadlineType.mechanism,
                )}
              />
            </Row>
            <Row label="Value">
              <Mono value={deadlineType.value} />
            </Row>
          </TableBody>
        </Table>
      )}

      <Typography gutterBottom variant="subtitle2" sx={styles.sectionHeading}>
        Duplicate
      </Typography>
      {isNil(duplicate) ? (
        <Typography variant="body2" color="text.secondary">
          Not planned (billing party abstained).
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            <Row label="Is duplicate">
              <Chip
                size="small"
                color={duplicate.isDuplicate ? "warning" : "default"}
                label={duplicate.isDuplicate ? "Yes" : "No"}
              />
            </Row>
            <Row label="Duplicate order uuid">
              <Mono value={duplicate.duplicateOrderUuid} />
            </Row>
            <Row label="Would adopt orphan uuid">
              <Mono value={duplicate.wouldAdoptOrphanUuid} />
            </Row>
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default AdhocDecisionPanel;
